import { useEffect, useState } from 'react';

/**
 * Dev/admin-only access flag for the TraineeDashboard "Development Plan" and
 * "Puzzles" tabs.
 *
 * These tabs are hidden from regular trainees. A developer or admin can reveal
 * (and reach) them by visiting any dashboard URL with `?devTabs=1`; the choice
 * is persisted in localStorage so it survives in-app navigation (React-Router
 * <Link>s drop the query string) and reloads. Visit `?devTabs=0` to opt out.
 *
 * This is NOT a security boundary — anyone who knows the param can enable it; it
 * only controls tab visibility and routing (the tab routes redirect to the
 * dashboard while the flag is off). Modelled on the Analysis Studio's
 * `useStudioDevAccess` (`?studioDev=1`), with its own storage key + param so the
 * two toggles are independent. Self-contained (no redux, no backend).
 */
const STORAGE_KEY = 'vega.dev.traineeDashboardTabs';
const PARAM = 'devTabs';

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Resolve the initial value synchronously: a `?devTabs=1|0` URL param wins and
 * is persisted, otherwise fall back to the stored flag. This MUST be synchronous
 * — the route guard (`index.tsx`) redirects during render, so a value that only
 * arrives via a later effect would bounce us off the page before it takes hold.
 */
const resolveInitial = (): boolean => {
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    if (value === '0' || value === 'false') {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    if (value !== null) {
      window.localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
  } catch {
    /* ignore quota / privacy-mode failures — in-memory state still resolves */
  }
  return readStored();
};

export function useDevTabsAccess(): boolean {
  const [enabled, setEnabled] = useState<boolean>(resolveInitial);

  useEffect(() => {
    // Keep multiple mounts / other tabs in sync if the flag is cleared.
    const sync = () => setEnabled(readStored());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return enabled;
}
